import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { useOrg } from '../org/OrgContext.jsx'

// Admin: the REDEMPTION LOG — every time a discount actually took money off
// a line, newest first.
//
// One row per usage, so a coupon left on an enrollment shows up again every
// month the billing job re-applies it. `code` and `label` are the SNAPSHOTS
// stored at redemption time: renaming a coupon later never rewrites its
// history. Read-only by design — nothing here can be edited or removed.
//
// Filters live in the URL (?discount_id=&kind=&from=&to=&q=), so the "uses"
// column on /admin/discounts links straight into a pre-filtered view and the
// admin can bookmark or share whatever they are looking at.

export function AdminDiscountLogPage() {
  const { t } = useTranslation()
  const { org } = useOrg()
  const { user, loading: authLoading } = useAuth()
  const [params, setParams] = useSearchParams()

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState({ count: 0, amount: 0 })
  const [discounts, setDiscounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // The URL is the single source of truth for the filters.
  const discountId = params.get('discount_id') || ''
  const kind       = params.get('kind') || ''
  const from       = params.get('from') || ''
  const to         = params.get('to') || ''
  const q          = params.get('q') || ''

  const [text, setText] = useState(q)

  function setFilter(patch) {
    const next = { discount_id: discountId, kind, from, to, q, ...patch }
    const clean = {}
    Object.entries(next).forEach(([k, v]) => { if (v) clean[k] = v })
    setParams(clean)
  }

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    api.admin.discounts.log({
      discount_id: discountId || undefined,
      kind: kind || undefined,
      from: from || undefined,
      to: to || undefined,
      q: q || undefined,
    })
      .then((d) => {
        setRows(d.redemptions || [])
        setTotal(d.total || { count: 0, amount: 0 })
        setDiscounts(d.discounts || [])
      })
      .catch((e) => setError(e.code || 'server'))
      .finally(() => setLoading(false))
  }, [discountId, kind, from, to, q])

  useEffect(() => {
    if (authLoading || !user || user.role !== 'admin') return
    load()
  }, [authLoading, user, load])

  useEffect(() => { setText(q) }, [q])

  function when(row) {
    return String(row.created_at || '').replace('T', ' ').slice(0, 16)
  }

  if (authLoading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!user || user.role !== 'admin') {
    return <main className="container"><p className="error">{t('admin.errors.not_admin')}</p></main>
  }

  return (
    <main className="container">
      <p><Link to="/admin/discounts">← {t('admin_discounts.title')}</Link></p>
      <h1>{t('admin_discounts.log_title')}</h1>
      <p className="muted">{t('admin_discounts.log_intro')}</p>
      <p className="muted small">{t('admin_discounts.log_note')}</p>

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ minWidth: '16rem' }}>
          <span>{t('admin_discounts.log_filter_discount')}</span>
          <select value={discountId} onChange={(e) => setFilter({ discount_id: e.target.value })}>
            <option value="">{t('admin_discounts.any')}</option>
            {discounts.map((d) => (
              <option key={d.id} value={d.id}>{d.name}{d.code ? ` (${d.code})` : ''}</option>
            ))}
          </select>
        </label>
        <label style={{ maxWidth: '12rem' }}>
          <span>{t('admin_discounts.log_filter_kind')}</span>
          <select value={kind} onChange={(e) => setFilter({ kind: e.target.value })}>
            <option value="">{t('admin_discounts.any')}</option>
            <option value="one_off">{t('admin_discounts.applies_one_off')}</option>
            <option value="monthly">{t('admin_discounts.applies_monthly')}</option>
          </select>
        </label>
        <label style={{ maxWidth: '12rem' }}>
          <span>{t('admin_discounts.log_filter_from')}</span>
          <input type="date" value={from} onChange={(e) => setFilter({ from: e.target.value })} />
        </label>
        <label style={{ maxWidth: '12rem' }}>
          <span>{t('admin_discounts.log_filter_to')}</span>
          <input type="date" value={to} onChange={(e) => setFilter({ to: e.target.value })} />
        </label>
        <label style={{ minWidth: '14rem' }}>
          <span>{t('admin_discounts.log_filter_q')}</span>
          <input
            type="text"
            value={text}
            placeholder={t('admin_discounts.log_filter_q_placeholder')}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') setFilter({ q: text.trim() }) }}
            onBlur={() => { if (text.trim() !== q) setFilter({ q: text.trim() }) }}
          />
        </label>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="button" className="link-button" onClick={() => setParams({})}>
            {t('admin_discounts.log_reset')}
          </button>
        </div>
      </div>

      {error && <p className="error">{t(`admin_discounts.errors.${error}`, {
        defaultValue: t(`admin.errors.${error}`, { defaultValue: error }),
      })}</p>}

      {loading && <p>{t('common.loading')}</p>}

      {!loading && (
        <>
          <p style={{ marginTop: '1rem' }}>
            <strong>{t('admin_discounts.log_total', { count: total.count })}</strong>
            {' — '}
            {t('admin_discounts.log_total_amount', {
              amount: total.amount, currency: org.currency_label,
            })}
          </p>

          {rows.length === 0
            ? <p className="muted">{t('admin_discounts.log_empty')}</p>
            : (
              <table className="results-table">
                <thead>
                  <tr>
                    <th>{t('admin_discounts.log_col_when')}</th>
                    <th>{t('admin_discounts.log_col_discount')}</th>
                    <th>{t('admin_discounts.log_col_user')}</th>
                    <th>{t('admin_discounts.log_col_participant')}</th>
                    <th>{t('admin_discounts.log_col_course')}</th>
                    <th>{t('admin_discounts.log_col_kind')}</th>
                    <th>{t('admin_discounts.log_col_amount')}</th>
                    <th>{t('admin_discounts.log_col_payment')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{when(r)}</td>
                      <td>
                        {r.discount_id
                          ? <Link to={`/admin/discounts/${r.discount_id}/edit`}>{r.label || r.discount_name}</Link>
                          : (r.label || '—')}
                        {r.code && <> <code>{r.code}</code></>}
                      </td>
                      <td>
                        <Link to={`/admin/users/${r.user_id}`}>{r.user_name}</Link>
                        <div className="muted small">{r.user_email}</div>
                      </td>
                      <td>{r.participant_name || '—'}</td>
                      <td>
                        {r.course_id
                          ? <Link to={`/admin/courses/${r.course_id}/edit`}>{r.course_name}</Link>
                          : '—'}
                      </td>
                      <td>{t(`admin_discounts.applies_${r.kind}`, { defaultValue: r.kind })}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>−{r.amount} {org.currency_label}</td>
                      <td>
                        {r.payment_id ? `#${r.payment_id}` : '—'}
                        {r.payment_status && (
                          <div className="muted small">
                            {t(`admin_payments.status_${r.payment_status}`, { defaultValue: r.payment_status })}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </>
      )}
    </main>
  )
}
